import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";

// =============================================================================
// useDMSamples - DM SAMPLE CONVERSATION FETCHER
// =============================================================================
// PURPOSE: Fetches conversation samples for DM sample modals.
//
// REQUIREMENTS:
// - Shows Instagram-style chat bubbles
// - Grouped by metric type (new DMs, engaged, response time, etc.)
// - Support follow-up sequences (different RPC function)
//
// KEY DECISIONS:
// - Uses RPC functions: get_dm_samples and get_followup_samples
// - Load more increases limit (no offset support in RPC)
// - AbortController cleanup prevents race conditions
// - Returns message frequency data for analysis
// =============================================================================

export type SampleType =
  | "new_dm"
  | "inbound_new_dm"
  | "manychat"
  | "manual_outbound"
  | "follow_up"
  | "engaged"
  | "real_convo"
  | "ghosted";

export interface MessageFrequency {
  first_message: string;
  frequency: number;
}

export interface SampleMessage {
  sender: "lead" | "setter" | "system";
  text: string;
  sent_at: string;
  has_media?: boolean;
}

export interface SampleConversation {
  conversation_id: string;
  contact_name: string;
  lead_instagram: string | null;
  first_msg_at: string;
  messages: SampleMessage[];
}

export interface FollowUpSequence {
  contact_name: string;
  lead_instagram: string | null;
  first_outreach: string;
  first_outreach_at: string;
  followup_message: string;
  followup_at: string;
  days_between: number;
  all_messages: SampleMessage[];
}

export interface DMSampleData {
  sample_type: string;
  total_count: number;
  message_frequency: MessageFrequency[];
  conversations: SampleConversation[];
}

export interface FollowUpSampleData {
  sample_type: string;
  total_count: number;
  conversations: FollowUpSequence[];
}

export const useDMSamples = (
  organizationId: string | null,
  dateRange: { start: Date | null; end: Date | null }
) => {
  const [data, setData] = useState<DMSampleData | FollowUpSampleData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const fetchSamples = useCallback(
    async (sampleType: SampleType, limit = 10) => {
      if (!supabase || !organizationId) {
        setError("No organization selected");
        return;
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        const startDate = dateRange.start ? new Date(dateRange.start) : null;
        const endDate = dateRange.end ? new Date(dateRange.end) : null;
        if (endDate) endDate.setHours(23, 59, 59, 999);

        if (sampleType === "follow_up") {
          // Use special follow-up function
          const { data: result, error: rpcError } = await supabase.rpc("get_followup_samples", {
            p_organization_id: organizationId,
            p_start_date: startDate?.toISOString() || null,
            p_end_date: endDate?.toISOString() || null,
            p_limit: limit,
          });

          if (!isMountedRef.current) return;

          if (rpcError) {
            logger.error("Follow-up samples RPC error:", rpcError);
            setError(rpcError.message);
            return;
          }

          setData(result as FollowUpSampleData);
        } else {
          // Use general samples function
          const { data: result, error: rpcError } = await supabase.rpc("get_dm_samples", {
            p_organization_id: organizationId,
            p_sample_type: sampleType,
            p_start_date: startDate?.toISOString() || null,
            p_end_date: endDate?.toISOString() || null,
            p_limit: limit,
          });

          if (!isMountedRef.current) return;

          if (rpcError) {
            logger.error("DM samples RPC error:", rpcError);
            setError(rpcError.message);
            return;
          }

          setData(result as DMSampleData);
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        logger.error("DM samples fetch error:", err);
        if (!isMountedRef.current) return;
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [organizationId, dateRange.start, dateRange.end]
  );

  const loadMore = useCallback(
    async (sampleType: SampleType, currentCount: number) => {
      if (!supabase || !organizationId || !data) return;

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        const startDate = dateRange.start ? new Date(dateRange.start) : null;
        const endDate = dateRange.end ? new Date(dateRange.end) : null;
        if (endDate) endDate.setHours(23, 59, 59, 999);

        if (sampleType === "follow_up") {
          // Fetch more by increasing limit
          const { data: result, error: rpcError } = await supabase.rpc("get_followup_samples", {
            p_organization_id: organizationId,
            p_start_date: startDate?.toISOString() || null,
            p_end_date: endDate?.toISOString() || null,
            p_limit: currentCount + 10,
          });

          if (!isMountedRef.current) return;

          if (rpcError) {
            setError(rpcError.message);
            return;
          }

          setData(result as FollowUpSampleData);
        } else {
          // Fetch more by increasing limit (no offset support)
          const { data: result, error: rpcError } = await supabase.rpc("get_dm_samples", {
            p_organization_id: organizationId,
            p_sample_type: sampleType,
            p_start_date: startDate?.toISOString() || null,
            p_end_date: endDate?.toISOString() || null,
            p_limit: currentCount + 10,
          });

          if (!isMountedRef.current) return;

          if (rpcError) {
            setError(rpcError.message);
            return;
          }

          setData(result as DMSampleData);
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        if (!isMountedRef.current) return;
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [organizationId, dateRange.start, dateRange.end, data]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return {
    data,
    loading,
    error,
    fetchSamples,
    loadMore,
    reset,
  };
};
